import { Users } from 'lucide-react';
import { format } from 'date-fns';

type ReferralStatus = 'invited' | 'signed_up' | 'completed' | 'active' | 'pending' | 'revoked';
type ReferralType = 'primary' | 'shared';

export interface Referral {
  id: string;
  email: string;
  status: ReferralStatus;
  refType?: ReferralType | string;
  accessType?: ReferralType | string;
  invitedDate: string;
  joinedDate?: string;
  totalSpent: number;
  commissionEarned: number;
}

interface ReferralsTableProps {
  referrals: Referral[];
}

const statusConfig: Record<string, { label: string; modifier: string }> = {
  invited: { label: 'Invited', modifier: 'neutral' },
  signed_up: { label: 'Signed Up', modifier: 'primary' },
  completed: { label: 'Active', modifier: 'success' },
  active: { label: 'Active', modifier: 'success' },
  pending: { label: 'Pending', modifier: 'warning' },
  revoked: { label: 'Revoked', modifier: 'danger' },
};

const refTypeConfig: Record<string, { label: string; modifier: string }> = {
  primary: { label: 'Primary', modifier: 'primary' },
  shared: { label: 'Shared', modifier: 'shared' },
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : format(date, 'MMM d, yyyy');
};

export function ReferralsTable({ referrals }: ReferralsTableProps) {
  if (referrals.length === 0) {
    return (
      <div className="referrals-empty-state">
        <span className="referrals-empty-state__icon">
          <Users aria-hidden="true" />
        </span>
        <h2>No referrals yet</h2>
        <p>Send your first invite to start earning commissions.</p>
      </div>
    );
  }

  return (
    <div className="referrals-table-scroll">
      <table className="referrals-table">
        <thead>
          <tr>
            <th scope="col">Email</th>
            <th scope="col">Status</th>
            <th scope="col">Ref Type</th>
            <th scope="col">Invited Date</th>
            <th scope="col">Joined Date</th>
            <th scope="col" className="is-numeric">Total Spent</th>
            <th scope="col" className="is-numeric">Commission Earned</th>
          </tr>
        </thead>
        <tbody>
          {referrals.map((referral) => {
            const status = statusConfig[referral.status] || statusConfig.invited;
            const refType = referral.refType || referral.accessType || 'primary';
            const type = refTypeConfig[refType] || refTypeConfig.primary;

            return (
              <tr key={referral.id}>
                <td className="referrals-table__email">{referral.email}</td>
                <td>
                  <span className={`referral-badge referral-badge--${status.modifier}`}>
                    {status.label}
                  </span>
                </td>
                <td>
                  <span className={`referral-badge referral-badge--outline-${type.modifier}`}>
                    {type.label}
                  </span>
                </td>
                <td>{formatDate(referral.invitedDate)}</td>
                <td className={!referral.joinedDate ? 'is-muted' : ''}>{formatDate(referral.joinedDate)}</td>
                <td className="is-numeric">
                  {referral.totalSpent > 0 ? `$${referral.totalSpent.toLocaleString()}` : '$0'}
                </td>
                <td className={`is-numeric${referral.commissionEarned > 0 ? ' is-positive' : ''}`}>
                  {referral.commissionEarned > 0
                    ? `$${referral.commissionEarned.toLocaleString()}`
                    : '$0'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
